import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ParagraphCtaInitModel,
  ParagraphCtaInitVariables,
  ParagraphCtaModel,
  ParagraphCtaVariables,
} from '../../../../models/email-template/paragraph-cta.interface';

/**
 * Mockup-styled Intro + CTA block - a drop-in replacement for app-paragraphcta
 * in block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-field-editor-v2 (incl. its segment dropdown/validation logic) and
 * app-pin sub-components and the exact same ParagraphCtaVariables field
 * names - only the surrounding layout is new. Comments are reachable via the
 * separate Comments tab (see edit-block-comments-v2), same as Main Banner.
 */
@Component({
  selector: 'app-paragraph-cta-v2',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FieldEditorV2Component,
    InlineEditV2Directive,
  ],
  templateUrl: './paragraph-cta-v2.component.html',
  styleUrl: './paragraph-cta-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ParagraphCtaComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ParagraphCtaModel = new ParagraphCtaInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ParagraphCtaInitVariables();
    }
  }

  introTextPlaceholder = 'Enter Intro Text';
  mainCtaTextPlaceholder = 'Enter Main CTA Text';
  mainCtaLinkPlaceholder = 'Enter Main CTA Link';

  /** Center-canvas card's own placeholder for the CTA button - shown as the
   *  button label itself (unlike the intro text's muted placeholder), since
   *  a CTA reads better as "this is where the button goes" than as empty. */
  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  /** The link opened by clicking the preview CTA - same treatment as Main
   *  Banner's previewHref: v1's exact helper.getFullUrl(MainCtaLink), plus a
   *  segment query param when a segment is selected (MainCtaLinkSegmentRegex
   *  is otherwise unused metadata today, v1 included). */
  get previewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['MainCtaLink']);
    const segment = this.block.variables?.['MainCtaLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ParagraphCtaVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
